"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_PREFIX = "onlyfish_visitor:";

type MessageRow = {
  id: string;
  fromFan: boolean;
  body: string;
  createdAt: string;
};

function visitorStorageKey(creatorSlug: string) {
  return `${STORAGE_PREFIX}${creatorSlug}`;
}

function loadOrCreateVisitorKey(creatorSlug: string): string {
  const key = visitorStorageKey(creatorSlug);
  try {
    let v = localStorage.getItem(key);
    if (!v || v.length < 8) {
      v =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(key, v);
    }
    return v;
  } catch {
    return `anon-${creatorSlug}-${Date.now()}`;
  }
}

type Props = { creatorSlug: string; displayName: string };

export function CreatorMessagesPanel({ creatorSlug, displayName }: Props) {
  const [visitorKey, setVisitorKey] = useState("");
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = useMemo(() => Boolean(visitorKey), [visitorKey]);

  useEffect(() => {
    setVisitorKey(loadOrCreateVisitorKey(creatorSlug));
  }, [creatorSlug]);

  const refresh = useCallback(async () => {
    if (!visitorKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/messages?creatorSlug=${encodeURIComponent(creatorSlug)}&visitorKey=${encodeURIComponent(visitorKey)}`,
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? res.statusText);
      }
      const data = (await res.json()) as MessageRow[];
      setMessages(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load messages");
    } finally {
      setLoading(false);
    }
  }, [creatorSlug, visitorKey]);

  useEffect(() => {
    if (!visitorKey) return;
    void refresh();
  }, [visitorKey, refresh]);

  async function send() {
    const body = draft.trim();
    if (!body || !visitorKey) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch("/api/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          creatorSlug,
          visitorKey,
          fromFan: true,
          body,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? res.statusText);
      }
      setDraft("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Send failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <p className="text-sm text-teal-200/80">
        <Link href={`/creators/${creatorSlug}`} className="hover:text-teal-100">
          ← {displayName}
        </Link>
      </p>
      <h1 className="mt-4 text-2xl font-semibold text-white">Message</h1>
      <p className="mt-2 text-sm leading-relaxed text-teal-100/75">
        Demo direct messages: your thread is stored per device via an anonymous
        id. Production would use sign-in, paid message tiers, and moderation.
      </p>

      <div className="mt-8 flex min-h-[280px] flex-col rounded-2xl border border-white/10 bg-black/20">
        <div className="max-h-[360px] flex-1 space-y-3 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-teal-200/70">Loading…</p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-teal-200/70">
              No messages yet. Say hello below.
            </p>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.fromFan ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                    m.fromFan
                      ? "bg-teal-400/25 text-teal-50"
                      : "border border-white/15 bg-white/5 text-teal-100"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-teal-200/50">
                    {m.fromFan ? "You" : displayName} ·{" "}
                    {new Date(m.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
        {error && (
          <p className="px-4 text-sm text-amber-200/90" role="alert">
            {error}
          </p>
        )}
        <div className="border-t border-white/10 p-4">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={!ready || sending}
            placeholder="Write a message…"
            rows={3}
            className="w-full resize-none rounded-xl border border-white/15 bg-[#042f2e]/80 px-4 py-3 text-sm text-white placeholder:text-teal-200/40 focus:border-teal-400/50 focus:outline-none focus:ring-1 focus:ring-teal-400/30 disabled:opacity-50"
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => void refresh()}
              className="rounded-lg px-4 py-2 text-sm text-teal-200 hover:bg-white/10"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={() => void send()}
              disabled={!ready || sending || !draft.trim()}
              className="rounded-xl bg-teal-400 px-5 py-2 text-sm font-semibold text-teal-950 disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
